import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createContactMessageAPI } from "../../helpers/api.ts";
import logodark from "../../assets/images/logo-dark.png";

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      message: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email")
        .required("Please Enter Your Email"),
      message: Yup.string()
        .min(10, "Message must be at least 10 characters")
        .required("Please Enter Your Message"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        
        const response = await createContactMessageAPI(values);
        
        if (response.success) {
          setSuccessMessage("Your message has been sent successfully! We'll get back to you soon.");
          validation.resetForm();
        } else {
          setErrorMessage(response.message || "Failed to send message. Please try again.");
        }
      } catch (error) {
        console.error("Contact form error:", error);
        if (error.response) {
          const { data } = error.response;
          setErrorMessage(data.message || data.error || "An error occurred while sending your message");
        } else {
          setErrorMessage("Unable to connect to the server. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    document.body.className = "bg-pattern";
    return () => {
      document.body.className = "";
    };
  }, []);

  return (
    <React.Fragment>
      <div className="bg-overlay"></div>
      <div className="account-pages my-5 pt-5 px-3 px-md-0">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10} xl={6}>
              <Card>
                <CardBody className="p-4">
                  <div>
                    <div className="text-center">
                      <Link to="/">
                        <img
                          src={logodark}
                          alt="dark logo"
                          height="100"
                          className="auth-logo logo-dark mx-auto"
                        />
                        <img
                          src={logodark}
                          alt="light logo"
                          height="100"
                          className="auth-logo logo-light mx-auto"
                        />
                      </Link>
                    </div>
                    <h4 className="font-size-18 text-muted mt-2 text-center">
                      Contact Us
                    </h4>
                    <p className="mb-5 text-center">
                      We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                    </p>
                    
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {successMessage && (
                        <Alert color="success">
                          <div className="text-center">{successMessage}</div>
                        </Alert>
                      )}
                      
                      {errorMessage && (
                        <Alert color="danger">
                          <div className="text-center">{errorMessage}</div>
                        </Alert>
                      )}
                      
                      <Row>
                        <Col md={12}>
                          <div className="mb-4">
                            <Label className="form-label">Email Address</Label>
                            <Input
                              name="email"
                              className="form-control"
                              placeholder="Enter your email address"
                              type="email"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              invalid={
                                validation.touched.email &&
                                validation.errors.email
                              }
                            />
                            {validation.touched.email &&
                              validation.errors.email && (
                                <FormFeedback type="invalid">
                                  {validation.errors.email}
                                </FormFeedback>
                              )}
                          </div>
                          
                          <div className="mb-4">
                            <Label className="form-label">Message</Label>
                            <Input
                              name="message"
                              className="form-control"
                              placeholder="Enter your message here..."
                              type="textarea"
                              rows="5"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.message || ""}
                              invalid={
                                validation.touched.message &&
                                validation.errors.message
                              }
                            />
                            {validation.touched.message &&
                              validation.errors.message && (
                                <FormFeedback type="invalid">
                                  {validation.errors.message}
                                </FormFeedback>
                              )}
                          </div>
                          
                          <div className="d-grid mt-4">
                            <Button
                              className="btn btn-primary waves-effect waves-light w-100 w-md-auto"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Sending...
                                </>
                              ) : (
                                "Send Message"
                              )}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                    
                    <div className="text-center w-100 my-2 mb-4">
                      <Link
                        to="/login"
                        className="btn btn-outline-primary w-100 w-md-auto"
                      >
                        Back to Login
                      </Link>
                    </div>
                    
                    <div className="d-flex justify-content-center gap-2 mb-4">
                      <Link to="/privacy-policy" className="text-muted">
                        Privacy Policy
                      </Link>
                      <span className="text-muted">|</span>
                      <Link to="/terms-conditions" className="text-muted">
                        Terms & Conditions
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>
              
              <div className="mt-5 text-center">
                <p className="text-white-50">
                  © {new Date().getFullYear()} Crafted with{" "}
                  <i className="mdi mdi-heart text-danger"></i> by Govindani Infotech Pvt. Ltd.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ContactUs;
